import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { SystemAPI, RunAPI } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatGauge } from '../components/StatGauge';
import { ServiceCard } from '../components/ServiceCard';
import { CertCard } from '../components/CertCard';
import { Skeleton } from '../components/ui/Skeleton';
import { Table, Thead, Tbody, Th, Tr, Td } from '../components/ui/Table';
import { StatusBadge } from '../components/StatusBadge';
import { formatUptime, formatDate, formatDuration } from '../lib/format';
import { TopBar } from '../components/TopBar';

export function DashboardPage() {
  const { data: sysInfo, isLoading: sysLoading } = useQuery({
    queryKey: ['system-info'],
    queryFn: SystemAPI.info,
    refetchInterval: 30000,
  });

  const { data: services, isLoading: svcLoading } = useQuery({
    queryKey: ['services'],
    queryFn: SystemAPI.services,
    refetchInterval: 30000,
  });

  const { data: runsData, isLoading: runsLoading } = useQuery({
    queryKey: ['runs', { limit: 10 }],
    queryFn: () => RunAPI.list({ limit: '10' }),
    refetchInterval: 30000,
  });

  const runs: any[] = runsData?.items ?? runsData ?? [];

  const [upgradeRunId, setUpgradeRunId] = useState<number | null>(null);

  const upgradeMutation = useMutation({
    mutationFn: SystemAPI.upgrade,
    onSuccess: (data) => setUpgradeRunId(data.runId),
  });

  // Опитуємо реальний run, поки apt upgrade крутиться у фоні —
  // POST /system/upgrade повертає runId одразу, а сам апдейт триває 10-50с.
  const { data: upgradeRun } = useQuery({
    queryKey: ['run', upgradeRunId],
    queryFn: () => RunAPI.get(upgradeRunId as number),
    enabled: upgradeRunId != null,
    refetchInterval: (query: any) => {
      const status = query.state.data?.status;
      return status && status !== 'RUNNING' ? false : 2000;
    },
  });

  const runStatus: string | undefined = upgradeRun?.status;
  // «Йде» = або ще не отримали runId (POST в польоті), або run у стані RUNNING.
  const upgradeInProgress =
    upgradeMutation.isPending ||
    (upgradeRunId != null && (!runStatus || runStatus === 'RUNNING'));
  const upgradeDone = runStatus === 'SUCCESS';
  const upgradeFailed = upgradeMutation.isError || runStatus === 'FAILED';
  const rebootRequired = typeof upgradeRun?.stdout === 'string'
    ? upgradeRun.stdout.includes('reboot required: yes')
    : false;

  // Лог «що саме оновилось» — беремо зі stdout рана.
  const upgradeLog = typeof upgradeRun?.stdout === 'string' ? upgradeRun.stdout : '';
  const aptSummary = upgradeLog.match(/\d+ upgraded, \d+ newly installed[^\n]*/)?.[0] || '';
  const claudeBefore = upgradeLog.match(/BEFORE:[^\n]*claude=(\S+)/)?.[1] || '';
  const claudeAfter = upgradeLog.match(/AFTER:[^\n]*claude=(\S+)/)?.[1] || '';

  const handleUpgrade = () => {
    if (!window.confirm('Оновити систему? Це може зайняти 3-10 хвилин.')) return;
    setUpgradeRunId(null);
    upgradeMutation.mutate();
  };

  const alerts: string[] = [];
  if (sysInfo?.disk?.usedPercent > 80) alerts.push(`Диск заповнений на ${sysInfo.disk.usedPercent.toFixed(0)}%`);
  const failedRecent = runs.filter((r: any) => r.status === 'FAILED').length;
  if (failedRecent > 0) alerts.push(`${failedRecent} задач провалилися за останній час`);

  return (
    <div>
      <TopBar title="Огляд" />
      <div className="p-6 space-y-4">
        {alerts.length > 0 && (
          <Link
            to="/tasks"
            search={{ status: 'FAILED' } as any}
            className="block"
          >
            <Card className="border-tg-warning hover:border-tg-accent transition-colors cursor-pointer">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-tg-warning font-medium">
                  <span>⚠️</span> Попередження
                </div>
                <span className="text-xs text-tg-accent">До задач →</span>
              </div>
              <ul className="space-y-1">
                {alerts.map((a, i) => (
                  <li key={i} className="text-sm text-tg-text">{a}</li>
                ))}
              </ul>
            </Card>
          </Link>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Система</CardTitle>
          </CardHeader>
          <p className="text-sm text-tg-text-secondary mb-3">
            Оновити всі пакети: apt upgrade + Claude Code CLI
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={handleUpgrade}
              disabled={upgradeInProgress}
              loading={upgradeInProgress}
            >
              🔄 Оновити систему
            </Button>
            {upgradeInProgress && (
              <span className="text-sm text-tg-text-secondary">
                ⏳ Оновлюю, зачекай (це 10-50с, не закривай){upgradeRunId != null ? ` — runId=${upgradeRunId}` : ''}
              </span>
            )}
            {upgradeDone && (
              <span className="text-sm text-tg-success">
                ✅ Оновлено{upgradeRunId != null ? ` (runId=${upgradeRunId})` : ''}
                {rebootRequired ? ' — VPS перезавантажується автоматично (~1 хв)' : ''}
              </span>
            )}
            {upgradeFailed && (
              <span className="text-sm text-tg-error">
                ❌ Помилка: {(upgradeMutation.error as Error)?.message || `run ${upgradeRunId} завершився з FAILED`}
              </span>
            )}
          </div>

          {upgradeRunId != null && upgradeLog && (
            <details className="mt-3">
              <summary className="text-sm text-tg-accent cursor-pointer select-none">
                📋 Що оновилось (лог runId={upgradeRunId})
              </summary>
              <div className="text-xs text-tg-text-secondary mt-2 space-y-1">
                {aptSummary && <div>📦 apt: {aptSummary}</div>}
                {claudeBefore && claudeAfter && (
                  <div>
                    🤖 Claude Code: {claudeBefore === claudeAfter
                      ? `${claudeAfter} (без змін)`
                      : `${claudeBefore} → ${claudeAfter}`}
                  </div>
                )}
              </div>
              <pre className="text-[11px] leading-snug bg-black/20 rounded p-2 mt-2 whitespace-pre-wrap overflow-x-auto max-h-72 overflow-y-auto">
                {upgradeLog}
              </pre>
            </details>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Сервер</CardTitle>
              {sysInfo && (
                <span className="text-xs text-tg-text-secondary">
                  {sysInfo.hostname || sysInfo.ip}
                </span>
              )}
            </CardHeader>
            {sysLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sysInfo ? (
              <div className="space-y-3">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-tg-text-secondary">IP</span>
                  <span className="text-tg-text font-mono">{sysInfo.ip || '—'}</span>
                </div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-tg-text-secondary">Uptime</span>
                  <span className="text-tg-text">{sysInfo.uptimeSeconds != null ? formatUptime(sysInfo.uptimeSeconds) : '—'}</span>
                </div>
                <StatGauge
                  label="CPU"
                  value={sysInfo.cpu?.usagePercent ?? 0}
                  detail={`Load: ${sysInfo.cpu?.loadAvg?.join(', ') ?? '—'}`}
                />
                <StatGauge
                  label="RAM"
                  value={sysInfo.ram?.usedPercent ?? 0}
                  detail={`${sysInfo.ram?.usedGb?.toFixed(1) ?? '?'} / ${sysInfo.ram?.totalGb?.toFixed(1) ?? '?'} GB`}
                />
                <StatGauge
                  label="Disk"
                  value={sysInfo.disk?.usedPercent ?? 0}
                  detail={`${sysInfo.disk?.usedGb?.toFixed(1) ?? '?'} / ${sysInfo.disk?.totalGb?.toFixed(1) ?? '?'} GB`}
                />
              </div>
            ) : (
              <p className="text-sm text-tg-text-secondary">Немає даних</p>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Сервіси</CardTitle>
            </CardHeader>
            {svcLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : (services?.length ?? 0) > 0 ? (
              <div>
                {(services as any[]).map((svc: any) => (
                  <ServiceCard key={svc.name} service={svc} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-tg-text-secondary">Сервіси не знайдені</p>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SSL Сертифікати</CardTitle>
            </CardHeader>
            {(services === undefined && sysLoading) ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <p className="text-sm text-tg-text-secondary">
                Перейдіть до розділу <a href="/certs">«Сертифікати»</a> для деталей.
              </p>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Останні запуски</CardTitle>
            </CardHeader>
            {runsLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : runs.length > 0 ? (
              <Table>
                <Thead>
                  <Tr>
                    <Th>Задача</Th>
                    <Th>Старт</Th>
                    <Th>Тривалість</Th>
                    <Th>Статус</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {runs.map((run: any) => (
                    <Tr key={run.id}>
                      <Td className="font-medium text-xs">{run.taskName || run.task?.name || '—'}</Td>
                      <Td className="text-tg-text-secondary text-xs">{formatDate(run.startedAt)}</Td>
                      <Td className="text-tg-text-secondary text-xs">
                        {run.durationMs != null ? formatDuration(run.durationMs) : '—'}
                      </Td>
                      <Td><StatusBadge status={run.status} /></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <p className="text-sm text-tg-text-secondary">Запуски відсутні</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
